import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import { getDisplayName } from '../../../../../models/NameUtils';
import { getDefaultDateStringValue } from '../../../../../models/UiUtils';
import {
	useCreateDonationMutation,
	useUpdateDonationMutation,
	invalidateAllDonationFilters,
	invalidateAllDonorFilters,
} from '../../../../../queries';
import CalendarIconUrl from '../../../../assets/icon_calendar.svg';
import { Checkbox } from '../../../../components/Checkbox';
import { CloseButton } from '../../../../components/CloseButton';
import { DayPicker } from '../../../../components/DayPicker';
import { DeprecatedPopover, PopoverType } from '../../../../components/DeprecatedPopover';
import { Modal } from '../../../../components/Modal';
import { TextInput } from '../../../../components/TextInput';
import { TextArea } from '../../../../components-v2/TextArea';
import { SimpleAutoCompleteSearchField } from '../../../../components/autocomplete/SimpleAutoCompleteSearchField';
import { DonationCampaignSearch } from '../../../../components/dataBoards/donations/DonationCampaignSearch';
import { ClearFieldIcon } from '../../../../components/svgs/icons/ClearFieldIcon';
import { DonationIcon } from '../../../../components/svgs/icons/DonationIcon';
import { TagsListWithTagsEditor } from '../../../../components/entities/tags/TagsEditor';
import { TagIcon } from '../../../../components/svgs/icons/TagIcon';
import { white, navigation } from '../../../../styles/colors';
import { baseStyleSheet } from '../../../../styles/styles';
import { styleSheet } from '../../EditModal/styles';

export const EditDonationModal = ({
	initialDonation,
	isOpen,
	setIsOpen,
	onSave,
	onCancel,
}: {
	initialDonation: Api.IDonation;
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	onSave: (donation: Api.IDonation) => void;
	onCancel: () => void;
}) => {
	const [donorName, setDonorName] = React.useState(
		initialDonation.donorName ?? (initialDonation.contact ? getDisplayName(initialDonation.contact) : '')
	);
	const [contact, setContact] = React.useState<Api.IContact>(initialDonation.contact ?? null);
	const [amount, setAmount] = React.useState<number>(initialDonation.amount ?? 0);
	const [campaign, setCampaign] = React.useState<Api.IDonationCampaign>(initialDonation.campaign ?? null);
	const [donationDate, setDonationDate] = React.useState<Date>(
		initialDonation.date ? moment(initialDonation.date).toDate() : null
	);
	const [donationDatePickerOpen, setDonationDatePickerOpen] = React.useState(false);
	const [tags, setTags] = React.useState<string[]>(initialDonation.tagsCollection?.map(tag => tag.name) ?? []);
	const [isInKindDonation, setIsInKindDonation] = React.useState<boolean>(Boolean(initialDonation.isInKindDonation));
	const [inKindDescription, setInKindDescription] = React.useState<string>(initialDonation.inKindDescription || '');
	const [details, setDetails] = React.useState<string>(initialDonation.details || '');

	const isEditing = !!initialDonation.id;

	// Reset form fields when initialDonation changes (for editing different donations)
	React.useEffect(() => {
		setDonorName(initialDonation.donorName ?? (initialDonation.contact ? getDisplayName(initialDonation.contact) : ''));
		setContact(initialDonation.contact ?? null);
		setAmount(initialDonation.amount ?? 0);
		setCampaign(initialDonation.campaign ?? null);
		setDonationDate(initialDonation.date ? moment(initialDonation.date).toDate() : null);
		setTags(initialDonation.tagsCollection?.map(tag => tag.name) ?? []);
		setIsInKindDonation(Boolean(initialDonation.isInKindDonation));
		setInKindDescription(initialDonation.inKindDescription || '');
		setDetails(initialDonation.details || '');
	}, [initialDonation]);

	const createDonationMutation = useCreateDonationMutation({
		onSuccess: (donation: Api.IDonation) => {
			toast.success('Donation successfully created');
			invalidateAllDonationFilters();
			invalidateAllDonorFilters();
			onSave(donation);
		},
	});

	const updateDonationMutation = useUpdateDonationMutation({
		onSuccess: (donation: Api.IDonation) => {
			toast.success('Donation successfully updated');
			invalidateAllDonationFilters();
			invalidateAllDonorFilters();
			onSave(donation);
		},
	});

	const save = () => {
		const campaignId = campaign?.id;

		const updatedDonation = {
			...initialDonation,
			donorName,
			amount,
			campaignId,
			date: moment(donationDate).format('YYYY-MM-DD'),
			contactId: contact.id,
			tags,
			isInKindDonation,
			inKindDescription: isInKindDonation ? inKindDescription : undefined,
			details: details || undefined,
		};

		if (isEditing) {
			updateDonationMutation.mutateAsync({ donation: updatedDonation });
			return;
		}
		createDonationMutation.mutateAsync({
			donation: updatedDonation,
		});
	};

	const cancel = () => {
		onCancel();
		setIsOpen(false);
	};

	const onContactCleared = () => {
		if (donorName === getDisplayName(contact)) {
			setDonorName('');
		}

		setContact(null);
	};

	const onContactSelected = ({ selection }: { selection: Api.IEntity }) => {
		setContact(selection);

		if (!donorName) {
			setDonorName(getDisplayName(selection));
		}
	};

	const onDonationDateSelected = (value: Date) => {
		setDonationDate(value);
	};

	const toggleDonationDatePickerOpen = () => {
		setDonationDatePickerOpen(!donationDatePickerOpen);
	};

	const clearDonationDate = (e: React.MouseEvent<HTMLElement>) => {
		// The clear button is nested in a clickable control, do not open the calendar if clear is clicked
		e.stopPropagation();
		e.preventDefault();

		setDonationDate(null);
	};

	const selectDonationCampaign = (donationCampaign: Api.IDonationCampaign) => {
		setCampaign(donationCampaign);
	};

	const onCreateCampaign = (result: Api.IDonationCampaign) => {
		setCampaign(result);
	};

	const onRequestAddTag = (tag: Api.IAccountTag) => {
		if (!tags.includes(tag.name)) {
			setTags([...tags, tag.name]);
		}
	};

	const onRequestRemoveTag = (tag: Api.IAccountTag) => {
		setTags(tags.filter(t => t !== tag.name));
	};

	const canSave = !!donorName && !!contact && amount >= 0 && !!donationDate;
	const isSaving = createDonationMutation.isPending || updateDonationMutation.isPending;

	const onClose = () => {
		setIsOpen(false);
	};

	return (
		<Modal
			className={css(styleSheet.modalContainer)}
			isOpen={isOpen}
			useDefaultHeader={true}
			onRequestClose={cancel}
			headerAccessory={
				<div className={css(styleSheet.modalHeader)}>
					<DonationIcon height={18} width={20} />
					<CloseButton onClick={onClose} fillColor={white} />
				</div>
			}
		>
			<article className={css(styleSheet.container)}>
				<h3 className={css(styleSheet.header)}>{`${isEditing ? 'Edit' : 'Add New'} Donation`}</h3>

				<div className={css(styleSheet.formControl)}>
					<label className={css(styleSheet.label)}>Contact</label>

					<div className={css(styleSheet.formControlInputWrap)}>
						<SimpleAutoCompleteSearchField
							onClear={onContactCleared}
							// TODO: Fix type mismatch between component's generic union type and our specific IEntity callback
							// @ts-ignore
							onItemSelected={onContactSelected}
							resultsLimit={5}
							pageSize={5}
							placeholder='Search'
							style={styleSheet.contactSearchField}
							type={Api.ResourceAutoCompleteViewModelType.Contact}
							initialSearchQuery={initialDonation.contact ? getDisplayName(initialDonation.contact) : ''}
						/>
					</div>
				</div>

				<div className={css(styleSheet.formControl)}>
					<label className={css(styleSheet.label)}>Donor Name / Company</label>

					<div className={css(styleSheet.formControlInputWrap)}>
						<TextInput
							inputId='donation-modal-edit-name'
							onChange={e => setDonorName(e.target.value)}
							type='text'
							value={donorName}
							placeholder='Name'
						/>
					</div>
				</div>

				<div className={css(styleSheet.formControl)}>
					<label className={css(styleSheet.label)}>Donation Amount</label>

					<div className={css(styleSheet.formControlInputWrap)}>
						<TextInput
							inputId='donation-modal-edit-amount'
							onChange={e => setAmount(parseFloat(e.target.value))}
							type='number'
							value={amount}
							placeholder='$'
							min={0}
							step='0.01'
						/>
					</div>
				</div>

				<div className={css(styleSheet.formControl)}>
					<div className={css(styleSheet.formControlInputWrap)}>
						<Checkbox
							checked={isInKindDonation}
							id='in-kind-donation-checkbox'
							onChange={e => setIsInKindDonation((e.target as HTMLInputElement).checked)}
						/>
						<label htmlFor='in-kind-donation-checkbox' className={css(styleSheet.checkboxLabel)}>
							This is an in-kind donation
						</label>
					</div>
				</div>

				{isInKindDonation && (
					<div className={css(styleSheet.formControl)}>
						<label className={css(styleSheet.label)}>In-Kind Description</label>

						<div className={css(styleSheet.formControlInputWrap)}>
							<TextInput
								inputId='in-kind-description-input'
								onChange={e => setInKindDescription(e.target.value)}
								type='text'
								value={inKindDescription}
								placeholder='e.g., Gift card, Volunteer time, Professional services'
							/>
						</div>
					</div>
				)}

				<div className={css(styleSheet.formControl)}>
					<label className={css(styleSheet.label)}>Campaign</label>

					<DonationCampaignSearch
						onSelectDonationCampaign={selectDonationCampaign}
						initialSearchFragment={initialDonation.campaign?.name}
						onCreateCampaign={onCreateCampaign}
						className={css(baseStyleSheet.fullWidth)}
					/>
				</div>

				<div className={css(styleSheet.formControl)}>
					<label className={css(styleSheet.label)}>Donation Date</label>

					<div
						className={css(styleSheet.formControlInputWrap, styleSheet.clickable)}
						onClick={toggleDonationDatePickerOpen}
					>
						<div className={css(styleSheet.dateContainer)}>
							<div className={css(styleSheet.dateFieldButton)}>
								<DeprecatedPopover
									anchor={<img src={CalendarIconUrl} />}
									dismissOnClickOutside={true}
									isOpen={donationDatePickerOpen}
									onRequestClose={toggleDonationDatePickerOpen}
									preferredPlacement='above'
									type={PopoverType.white}
								>
									<DayPicker allowPastDates={true} onDayClick={onDonationDateSelected} selectedDays={donationDate} />
								</DeprecatedPopover>
								{!!donationDate && <span>{getDefaultDateStringValue(donationDate)}</span>}
							</div>
							<button onClick={(e: React.MouseEvent<HTMLElement>) => clearDonationDate(e)}>
								<ClearFieldIcon />
							</button>
						</div>
					</div>
				</div>

				<div className={css(styleSheet.formControl)}>
					<label className={css(styleSheet.label)}>Tags</label>

					<div className={css(styleSheet.formControlInputWrap)}>
						<div className={css(baseStyleSheet.flex, baseStyleSheet.justifyStart)}>
							<TagIcon
								className={css(baseStyleSheet.flexShrink0, baseStyleSheet.mr4, baseStyleSheet.mt3, baseStyleSheet.w4)}
								fillColor={navigation}
							/>
							{(() => {
								// Use tagsCollection if available (when editing existing donation), otherwise create basic tag objects
								const editorTags = tags.map(tagName => {
									// Try to find the tag in the original tagsCollection to preserve color info
									const existingTag = initialDonation.tagsCollection?.find(tag => tag.name === tagName);
									return existingTag || { name: tagName, id: tagName };
								});

								return (
									<TagsListWithTagsEditor
										tagType={Api.ResourceAutoCompleteViewModelType.AccountTag}
										className={css(baseStyleSheet.flex1)}
										disabled={false}
										entity={null}
										onRequestAddTag={onRequestAddTag}
										onRequestRemoveTag={onRequestRemoveTag}
										tags={editorTags}
									/>
								);
							})()}
						</div>
					</div>
				</div>

				<div className={css(styleSheet.formControl)}>
					<label className={css(styleSheet.label)}>Details</label>

					<div className={css(styleSheet.formControlInputWrap)}>
						<TextArea
							value={details}
							onChange={e => setDetails(e.target.value)}
							placeholder='Add any additional details about this donation (e.g., check number, notes)...'
							rows={3}
						/>
					</div>
				</div>

				<footer className={css(styleSheet.footer)}>
					<button className={css(baseStyleSheet.ctaButton)} onClick={save} disabled={!canSave || isSaving}>
						{isSaving ? 'Saving...' : 'Save'}
					</button>
					<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={cancel} disabled={isSaving}>
						Cancel
					</button>
				</footer>
			</article>
		</Modal>
	);
};
